// src/components/common/DataTable.jsx
// -----------------------***************-----------------------
// A reusable data table component using Cloudscape Design System.
// Supports dynamic columns, client-side filtering/sorting/pagination, and CSV export.
// ------------------------***************-----------------------
import { useState, useEffect, useMemo } from "react";
import {
  Header,
  Box,
  Button,
  Table,
  TextFilter,
  Pagination,
  CollectionPreferences,
} from "@cloudscape-design/components";
import { useCollection } from "@cloudscape-design/collection-hooks";

export default function DataTable({ columns, data, rowCount, columnHighlights = {}, title }) {
  const [preferences, setPreferences] = useState({
    pageSize: 50,
    visibleContent: columns,
  });

  // Sync visible columns when a new result arrives
  useEffect(() => {
    setPreferences((p) => ({ ...p, visibleContent: columns }));
  }, [columns.join(",")]);

  const columnDefinitions = useMemo(() =>
    columns.map((col) => ({
      id: col,
      header: col,
      // cell: (row) => String(row[col] ?? ""),
      cell: (row) => {
        const val = String(row[col] ?? "");
        const bg  = columnHighlights[col];
        if (bg) {
          return (
            <span style={{
              background: bg,
              color: "#fff",
              borderRadius: 4,
              padding: "2px 10px",
              fontWeight: 600,
              fontSize: 12,
              display: "inline-block",
            }}>
              {val}
            </span>
          );
        }
        return val;
      },
      sortingField: col,
      isRowHeader: false,
    })),
    [columns, columnHighlights]
  );

  const { items, filterProps, paginationProps, collectionProps } = useCollection(data, {
    filtering: { empty: "No results", noMatch: "No results match your filter" },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
  });

  const visibleColumns = columnDefinitions.filter((c) =>
    preferences.visibleContent.includes(c.id)
  );

  const downloadCSV = () => {
    const header = preferences.visibleContent.join(",");
    const rows = data.map((row) =>
      preferences.visibleContent.map((col) => {
        const val = String(row[col] ?? "");
        const escaped = val.replace(/"/g, '""');
        return (val.includes(",") || val.includes('"') || val.includes("\n"))
          ? `"${escaped}"` : val;
      }).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `results_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    // Wrapper div is required — Cloudscape <Table> ignores the style prop.
    // This is the correct way to constrain height and enable scrolling.
    <div style={{ maxHeight: 500, overflowY: "auto", overflowX: "auto" }}>
      <Table
        {...collectionProps}
        columnDefinitions={visibleColumns}
        items={items}
        loadingText="Loading results"
        stripedRows
        stickyHeader
        resizableColumns
        variant="container"
        filter={<TextFilter {...filterProps} filteringPlaceholder="Search results..." />}
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={{
              title: "Rows per page",
              options: [
                { value: 25,  label: "25 rows"  },
                { value: 50,  label: "50 rows"  },
                { value: 100, label: "100 rows" },
                { value: 500, label: "500 rows" },
              ],
            }}
            visibleContentPreference={{
              title: "Select visible columns",
              options: [{ label: "Columns", options: columns.map((c) => ({ id: c, label: c })) }],
            }}
          />
        }
        header={
          <Header
            counter={`(${rowCount} rows)`}
            actions={
              <Button iconName="download" onClick={downloadCSV}>
                Download
              </Button>
            }
          >
            Results
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            <b>No results</b>
          </Box>
        }
      />
    </div>
  );
}